import { FC } from 'react';
import { Bar, BarChart, Cell, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { ChartEmptyState } from '../../leopart-update/components/AthleteInjuryCharts/charts/_shared/ChartEmptyState';
import {
  chartAxisTick,
  chartDataLabelStyle,
  chartTooltipContentStyle,
  chartTooltipItemStyle,
  chartTooltipLabelStyle,
} from './chartTheme';
import { DistributionSlice } from './chart.types';
import styles from './DistributionBars.module.scss';

interface IDistributionBarsProps {
  data: DistributionSlice[];
  /** Formatta il valore grezzo (es. minuti → "1h 20m"). Default: numero puro. */
  valueFormatter?: (value: number) => string;
  /**
   * Cosa stampare a fine barra:
   * - `'percent'` (default): solo la percentuale — comportamento storico.
   * - `'value-percent'`: valore formattato + percentuale (es. "1h 20m · 35%"),
   *   quando il numero grezzo (i minuti) conta quanto la quota.
   */
  labelMode?: 'percent' | 'value-percent';
  /** Etichetta del valore nel tooltip (default "Minuti"). */
  tooltipLabel?: string;
}

/**
 * Barre orizzontali generiche, una riga per voce, con la percentuale già
 * calcolata (`slice.percent`) stampata a fine barra — mai ricalcolata qui.
 *
 * Promosse da `MethodDistributionChart` (TrainingStatisticsDashboard): stessa
 * impaginazione (altezza proporzionale al numero di righe), generalizzata su
 * `DistributionSlice`.
 */

/** Lunghezza massima dell'etichetta categoria sull'asse Y prima del troncamento con ellissi. */
const MAX_LABEL_CHARS = 22;

/** Tronca un'etichetta lunga aggiungendo un'ellissi, il testo intero resta nel `<title>` nativo. */
function truncateLabel(label: string): string {
  return label.length > MAX_LABEL_CHARS ? `${label.slice(0, MAX_LABEL_CHARS - 1)}…` : label;
}

/**
 * Tick Y custom: alcune etichette di categoria sono lunghe (livelli Mallo,
 * flussi di gioco) e condividono lo spazio con le barre. Le tronchiamo con
 * ellissi e lasciamo il testo intero in un `<title>` nativo (tooltip del
 * browser al passaggio del mouse), senza introdurre un secondo tooltip.
 */
const CategoryTick: FC<{ x?: number; y?: number; payload?: { value: string } }> = ({
  x = 0,
  y = 0,
  payload,
}) => {
  const label = String(payload?.value ?? '');
  return (
    <text x={x} y={y} dominantBaseline="central" textAnchor="end" style={chartAxisTick}>
      {truncateLabel(label)}
      <title>{label}</title>
    </text>
  );
};

export const DistributionBars: FC<IDistributionBarsProps> = ({
  data,
  valueFormatter,
  labelMode = 'percent',
  tooltipLabel = 'Minuti',
}) => {
  if (!data || data.length === 0) return <ChartEmptyState />;
  const format = valueFormatter ?? ((value: number) => String(value));
  const withValue = labelMode === 'value-percent';

  /**
   * In modalità "value-percent" servono DUE campi (valore + quota) nella
   * stessa etichetta: `LabelList` con `dataKey` ne espone uno solo, quindi
   * il testo si compone qui via `content`, leggendo la fetta per indice.
   */
  const renderCombinedLabel = (props: {
    x?: string | number;
    y?: string | number;
    width?: string | number;
    height?: string | number;
    index?: number;
  }) => {
    const slice = props.index != null ? data[props.index] : undefined;
    if (!slice) return null;
    const pct = slice.percent == null ? '—' : `${slice.percent}%`;
    const text = `${format(slice.value)} · ${pct}`;
    const tx = Number(props.x ?? 0) + Number(props.width ?? 0) + 8;
    const ty = Number(props.y ?? 0) + Number(props.height ?? 0) / 2;
    return (
      <text x={tx} y={ty} dominantBaseline="central" style={chartDataLabelStyle}>
        {text}
      </text>
    );
  };

  return (
    <div className={styles.container}>
      <ResponsiveContainer width="100%" height={Math.max(160, data.length * 44)}>
        <BarChart data={[...data]} layout="vertical" margin={{ top: 4, right: withValue ? 120 : 60, bottom: 4, left: 8 }}>
          <XAxis type="number" hide />
          <YAxis type="category" dataKey="label" width={150} tick={<CategoryTick />} />
          <Tooltip
            contentStyle={chartTooltipContentStyle}
            labelStyle={chartTooltipLabelStyle}
            itemStyle={chartTooltipItemStyle}
            formatter={(value) => [format(Number(value)), tooltipLabel]}
          />
          <Bar dataKey="value" radius={[0, 3, 3, 0]} maxBarSize={22}>
            {data.map((slice) => (
              <Cell key={slice.id} fill={slice.color} />
            ))}
            {withValue ? (
              <LabelList dataKey="value" position="right" content={renderCombinedLabel} />
            ) : (
              <LabelList
                dataKey="percent"
                position="right"
                formatter={(value: unknown) => (value == null ? '—' : `${value}%`)}
                style={chartDataLabelStyle}
              />
            )}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default DistributionBars;
